import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, Building2, Bot, FileUp, Users, UserCheck, MessagesSquare, BarChart3, CreditCard, QrCode, Shield, LogOut, Menu, X, Mail, KeyRound, UserPlus, Code2, Palette, ShoppingBag, Truck, MessageSquare, Tag, Target, FileBarChart, Boxes } from "lucide-react";
import { cn } from "../lib/utils";
import ImpersonationBanner from "./ImpersonationBanner";
import CashTenderWatcher from "./CashTenderWatcher";
import RudrasLogo from "./RudrasLogo";
import api from "../lib/api";

const items = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
  { to: "/dashboard/profile", icon: Building2, label: "Business Profile" },
  { to: "/dashboard/resources", icon: FileUp, label: "Resources & AI Assistant" },
  // Minimum Usable ICP Vertical Slice (post-Step-1B) — Ideal Customer
  // Profile Library: create/publish ICPs, add Buyer Personas, connect a
  // published ICP to a Sales Assistant.
  { to: "/dashboard/icp", icon: Target, label: "ICP Library" },
  { to: "/dashboard/chatbot", icon: Palette, label: "Branding" },
  { to: "/dashboard/leads", icon: Users, label: "Leads" },
  // Phase 1 Customer Foundation (Feb 2026)
  { to: "/dashboard/customers", icon: UserCheck, label: "Customers" },
  // Phase 2A Commerce Foundation (Feb 2026)
  { to: "/dashboard/commerce-settings", icon: Tag, label: "Commerce" },
  { to: "/dashboard/conversations", icon: MessagesSquare, label: "Conversations" },
  { to: "/dashboard/email-logs", icon: Mail, label: "Email Logs" },
  // Feb 2026 UI cleanup — Share Link / QR and Embed are hidden from the
  // sidebar (routes preserved at /dashboard/share and /dashboard/embed so
  // existing deep links keep working). Tenants reach Share via the Public
  // assistant link panel on the Overview page; Embed is internal-tooling.
  // { to: "/dashboard/share", icon: QrCode, label: "Share Link / QR" },
  // { to: "/dashboard/embed", icon: Code2, label: "Embed" },
  { to: "/dashboard/subscription", icon: CreditCard, label: "Billing" },
  { to: "/dashboard/usage", icon: BarChart3, label: "Usage & Billing" },
  { to: "/dashboard/team", icon: UserPlus, label: "Team" },
  { to: "/dashboard/security", icon: KeyRound, label: "Account & Security" },
  { to: "/dashboard/privacy", icon: Shield, label: "Privacy & Data" },
];

// Phase 3D — Retail Orders. Only injected into the sidebar when the
// authenticated tenant has `retail_mode_enabled = true`.
const RETAIL_NAV_ITEM = { to: "/dashboard/retail-orders", icon: ShoppingBag, label: "Retail Orders" };

// Retail Reporting Sprint 1 (Oct 2026) — Reports sits directly under
// Overview, only for tenants the server resolves to the Retail Operations
// experience (`GET /company` -> `dashboard_experience`).
const REPORTS_NAV_ITEM = { to: "/dashboard/reports", icon: FileBarChart, label: "Reports" };

// Phase 3G — Retail Catalog setup nav. Same conditional rendering as above.
// Put the Catalog item BEFORE Orders in the sidebar because logically you
// create a catalog before any orders flow through.
const RETAIL_CATALOG_NAV_ITEM = { to: "/dashboard/retail-catalogs", icon: ShoppingBag, label: "Retail Catalog" };

// Inventory Engine Phase 1 (Oct 2026) — items, packaging, locations and the
// stock ledger. Retail tenants only; the page itself shows only Setup until
// the tenant enables inventory.
const INVENTORY_NAV_ITEM = { to: "/dashboard/inventory", icon: Boxes, label: "Inventory" };

// Phase 3L.1 — Retail Link Profiles. A tenant can spin up multiple
// customer-facing storefronts (Kiosk, QR Table Menu, Pickup, Catering…)
// using the same catalogs. Sits between Catalog setup and live Orders.
const RETAIL_LINKS_NAV_ITEM = { to: "/dashboard/retail-link-profiles", icon: ShoppingBag, label: "Retail Links" };

// P1.4a — Shipping admin. Platform-level service but surfaced in the
// sidebar only for retail tenants (storefronts ship physical product).
const SHIPPING_NAV_ITEM = { to: "/dashboard/shipping", icon: Truck, label: "Shipping" };

// Parcel Shipping Phase 1A CP1 — customer parcel-shipping foundation
// (tenant config + presets + readiness). Distinct from ShippingAdmin
// which remains the staff-operated carrier console.
const PARCEL_SHIPPING_NAV_ITEM = { to: "/dashboard/parcel-shipping", icon: Truck, label: "Parcel Shipping" };

// Gate B2 blocker-resolution (Phase 3A.6) — Local Delivery provider
// configuration (pickup origin/contact + Uber Direct TEST credentials).
// Distinct from SHIPPING_NAV_ITEM/PARCEL_SHIPPING_NAV_ITEM per Standards
// §21.2 ("keep local delivery separate from nationwide parcel shipping
// in UI, data model, and operational reporting").
const LOCAL_DELIVERY_NAV_ITEM = { to: "/dashboard/local-delivery", icon: Truck, label: "Local Delivery" };

// SMS Notifications Foundation — Messaging admin. Platform-level
// service available to ALL tenants (not retail-only).
const MESSAGING_NAV_ITEM = { to: "/dashboard/messaging", icon: MessageSquare, label: "Messaging" };
// Print Service Framework — Phase 1 (Feb 2026). Industry-agnostic; the
// page shows agent tokens, registered printers, print queue, reprints.
const PRINTING_NAV_ITEM = { to: "/dashboard/printing", icon: MessageSquare, label: "Printing" };

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [retailEnabled, setRetailEnabled] = useState(false);
  const [reportsEnabled, setReportsEnabled] = useState(false);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Phase 3D — fetch the retail-mode flag once per dashboard mount. We
  // keep this in a *separate* effect from anything else so a failure
  // (e.g. 401 during initial bootstrap) silently leaves the Retail nav
  // hidden rather than blanking the sidebar.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/company").then((r) => {
      if (!alive) return;
      setRetailEnabled(Boolean(r?.data?.company?.retail_mode_enabled));
      setReportsEnabled(r?.data?.dashboard_experience === "retail_operations");
    }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Phase 3L.6 — Retail Link Profiles is now an internal/admin concept;
  // the tenant-facing workflow lives inside each Catalog. We keep the
  // route reachable (admin debugging) but only surface it in the
  // sidebar when ?admin=1 is in the URL. Normal tenant view sees just
  // Catalog + Orders.
  const showAdminInternals = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("admin") === "1";
  const baseItems = reportsEnabled ? [items[0], REPORTS_NAV_ITEM, ...items.slice(1)] : items;
  const navItems = retailEnabled
    ? (showAdminInternals
        ? [...baseItems, RETAIL_CATALOG_NAV_ITEM, INVENTORY_NAV_ITEM, RETAIL_LINKS_NAV_ITEM, RETAIL_NAV_ITEM, LOCAL_DELIVERY_NAV_ITEM, SHIPPING_NAV_ITEM, PARCEL_SHIPPING_NAV_ITEM, MESSAGING_NAV_ITEM, PRINTING_NAV_ITEM]
        : [...baseItems, RETAIL_CATALOG_NAV_ITEM, INVENTORY_NAV_ITEM, RETAIL_NAV_ITEM, LOCAL_DELIVERY_NAV_ITEM, SHIPPING_NAV_ITEM, PARCEL_SHIPPING_NAV_ITEM, MESSAGING_NAV_ITEM, PRINTING_NAV_ITEM])
    : [...baseItems, MESSAGING_NAV_ITEM, PRINTING_NAV_ITEM];

  return (
    <div
      className={cn(
        // Phase 3L.1 Pass B — `lg:h-screen lg:overflow-hidden` pins the
        // layout to the viewport on tablet/desktop so the sidebar nav
        // and the main content area each get their own independent
        // vertical scrollbar (per <aside>.<nav> and <main> respectively).
        // Mobile keeps `min-h-screen` so the page scrolls as one — we
        // never want nested-scroll chaos on small screens.
        "min-h-screen lg:h-screen lg:overflow-hidden flex bg-background",
        user?.imp && "ring-4 ring-amber-400 ring-inset"
      )}
      data-impersonating={user?.imp ? "true" : "false"}
    >
      {/* Mobile topbar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b flex items-center justify-between px-4">
        {/* v1.0.16 — static branding only; logo must NOT navigate away from
            the tenant dashboard. */}
        <div className="flex items-center gap-2" data-testid="mobile-logo">
          <RudrasLogo size="sm" />
          <span className="font-display font-semibold">Rudras-AI</span>
        </div>
        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(true)}
          className="p-2 -mr-2 rounded-md hover:bg-secondary"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* Backdrop (mobile) */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside
        data-testid="sidebar"
        className={cn(
          "z-50 flex flex-col w-64 flex-shrink-0",
          // Phase 3L.1 Pass B — on lg+ the sidebar fills the viewport
          // height so its <nav> can scroll independently of <main>. The
          // explicit `lg:overflow-y-auto` here is the safety net for
          // very tall nav lists on shorter laptop screens (1366×768).
          "lg:h-screen lg:overflow-y-auto",
          "bg-gradient-to-b from-white via-slate-50/40 to-slate-50/70 border-r border-slate-200/70",
          "shadow-[inset_-1px_0_0_rgba(15,23,42,0.04)]",
          "fixed inset-y-0 left-0 transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
          "lg:static lg:translate-x-0"
        )}
      >
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between border-b border-slate-200/60">
          {/* v1.0.16 — static branding only; logo must NOT navigate away from
              the tenant dashboard. Public landing logo behavior is untouched. */}
          <div className="flex items-center gap-2" data-testid="sidebar-logo">
            <RudrasLogo size="md" />
            <span className="font-display font-semibold text-lg tracking-tight">Rudras-AI</span>
          </div>
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="lg:hidden p-1.5 -mr-1 rounded-md hover:bg-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((it) => {
            const active = loc.pathname === it.to
              || (it === REPORTS_NAV_ITEM && loc.pathname.startsWith("/dashboard/reports/"));
            return (
              <Link key={it.to} to={it.to}
                data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className={cn(
                  "group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
                  active
                    ? "bg-accent text-accent-foreground font-medium shadow-[0_2px_8px_-4px_rgba(14,92,78,0.45)]"
                    : "text-muted-foreground hover:bg-white hover:text-foreground hover:shadow-sm hover:translate-x-[1px]"
                )}>
                {/* Subtle accent rail on active item */}
                {active && (
                  <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-accent-foreground/30" />
                )}
                <it.icon className={cn("w-4 h-4 transition-transform", !active && "group-hover:scale-110")} />
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200/60 bg-white/60">
          <div className="px-3 py-2 text-xs">
            <div className="font-medium truncate text-slate-900">{user?.full_name}</div>
            <div className="text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Dashboard-wide cash-tender confirmation (Sep 2026). Mounted on
          the DASHBOARD SHELL, so it follows staff across every
          `/dashboard/*` screen — and, by the same structure, never
          appears on the customer storefront, the public order-status
          page, the Kitchen Display or Internal Admin, none of which
          render through this layout. Gated on the same
          `retail_mode_enabled` flag the Retail nav is: a non-retail
          tenant never polls for cash orders at all.

          It WRAPS the page content rather than sitting beside it so that
          a dashboard page (Retail Orders) can reach the one controller
          through `useCashTender()` and bring an order to the front of
          the shared queue, instead of opening a second, competing cash
          dialog of its own. Exactly one `CashTenderDialog` is ever
          mounted under this shell. */}
      <CashTenderWatcher enabled={retailEnabled}>
        <main className="flex-1 overflow-y-auto pt-14 lg:pt-0 lg:h-screen">
          <ImpersonationBanner />
          <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-10">{children}</div>
        </main>
      </CashTenderWatcher>
    </div>
  );
}
